import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import {
  GainCatalog,
  GainFormulaInstance,
  GainFormulaInstanceRequest,
} from '../../../../../models/gain-formula-instance.model';
import {
  GainFormulaTemplate,
  GainFormulaTemplateType,
} from '../../../../../models/gain-formula-template.model';
import {
  GainParameter,
  GainParameterType,
} from '../../../../../models/gain-parameter.model';
import { Rule } from '../../../../../models/rule.model';
import { GainFormulaInstanceService } from '../../../../../services/gain-formula-instance.service';

const ALL_PLANTS_LABEL = 'Toutes les usines';

export interface GainInstancesState {
  requests: GainFormulaInstanceRequest[];
  dirty: boolean;
  hasErrors: boolean;
  // False when the existing instances could not be loaded: the request must then leave them untouched.
  loaded: boolean;
}

interface DraftOption {
  label: string;
  value: string;
}

interface DraftParameter {
  parameterUid: string;
  name: string;
  type: GainParameterType;
  // List parameters pick a predefined value; Decimal parameters take a typed value.
  selectedValueUid: string | null;
  originalValueUid: string | null;
  typedValue: number | null;
  options: DraftOption[];
  parameterIsDeleted: boolean;
  valueIsDeleted: boolean;
  valueMatchesPlant: boolean;
}

interface DraftInstance {
  uid: string | null;
  templateUid: string;
  templateGroupId: string;
  templateName: string;
  typePath: string;
  equation: string;
  templateVersion: number;
  latestVersion: number;
  templateIsDeleted: boolean;
  parameters: DraftParameter[];
}

interface Marker {
  label: string;
  severity: 'success' | 'info' | 'warn' | 'danger';
  tooltip?: string;
}

@Component({
  selector: 'app-gain-formula-instances-step',
  templateUrl: './gain-formula-instances-step.component.html',
  standalone: false,
})
export class GainFormulaInstancesStepComponent implements OnInit, OnDestroy {
  @Input() formGroup!: FormGroup;
  @Input() rule?: Rule;
  @Input() mode: 'create' | 'edit' = 'edit';
  @Output() stateChange = new EventEmitter<GainInstancesState>();

  catalog: GainCatalog = { types: [], templates: [], parameters: [] };
  drafts: DraftInstance[] = [];
  templateNodes: TreeNode[] = [];
  pickerValue: TreeNode | null = null;
  loading = true;
  loadError: string | null = null;
  dirty = false;

  private loaded = false;
  private destroy$ = new Subject<void>();

  constructor(
    private instanceService: GainFormulaInstanceService,
    private cdr: ChangeDetectorRef,
  ) {}

  get plant(): string {
    return this.formGroup.get('plant')?.value || '';
  }

  get canAdd(): boolean {
    return !!this.plant && !!this.formGroup.get('functionalLocation')?.value;
  }

  ngOnInit(): void {
    const ruleUid = this.mode === 'edit' ? this.rule?.uid : undefined;

    forkJoin({
      catalog: this.instanceService.getCatalog(),
      existing: ruleUid
        ? this.instanceService.getByRule(ruleUid).pipe(
            catchError(() => {
              this.loadError =
                'Impossible de charger les formules de gain existantes ; elles ne seront pas modifiées.';
              return of(null);
            }),
          )
        : of({ instances: [], totalAmount: null }),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ catalog, existing }) => {
          this.catalog = catalog;
          if (existing) {
            this.drafts = existing.instances.map((i) => this.fromInstance(i));
            this.loaded = true;
          }
          this.loading = false;
          this.rebuildTemplateNodes();
          this.emitState();
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.loadError = 'Impossible de charger le catalogue des gains.';
          this.emitState();
          this.cdr.markForCheck();
        },
      });

    // A fresh draft list can only be re-filtered by plant while no instance exists (the plant is locked otherwise).
    this.formGroup
      .get('plant')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.drafts.forEach((d) =>
          d.parameters.forEach((p) => (p.options = this.optionsFor(p))),
        );
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---- Template picker ----

  onTemplateSelected(event: { node: TreeNode }): void {
    this.pickerValue = null;
    if (!this.canAdd) return;

    // A type node adds every addable template beneath it (sub-types included).
    const templates = this.collectSelectableTemplates(event.node);
    if (templates.length === 0) return;

    this.drafts = [...this.drafts, ...templates.map((t) => this.newDraft(t))];
    this.dirty = true;
    this.rebuildTemplateNodes();
    this.emitState();
  }

  removeDraft(draft: DraftInstance): void {
    this.drafts = this.drafts.filter((d) => d !== draft);
    this.dirty = true;
    this.rebuildTemplateNodes();
    this.emitState();
  }

  onValueChange(): void {
    this.dirty = true;
    this.emitState();
  }

  // ---- Markers ----

  markers(draft: DraftInstance): Marker[] {
    const result: Marker[] = [];
    const untouched = (p: DraftParameter) =>
      p.selectedValueUid === p.originalValueUid;

    if (!draft.uid) {
      result.push({ label: 'Nouvelle', severity: 'info' });
      return result;
    }

    if (draft.latestVersion > draft.templateVersion) {
      result.push({
        label: `Version obsolète (v${draft.templateVersion} → v${draft.latestVersion})`,
        severity: 'warn',
        tooltip: 'Un modèle plus récent existe pour cette formule.',
      });
    }
    if (draft.templateIsDeleted) {
      result.push({ label: 'Modèle supprimé', severity: 'danger' });
    }
    if (draft.parameters.some((p) => untouched(p) && p.parameterIsDeleted)) {
      result.push({ label: 'Paramètre supprimé', severity: 'danger' });
    }
    if (draft.parameters.some((p) => untouched(p) && p.valueIsDeleted)) {
      result.push({ label: 'Valeur supprimée', severity: 'danger' });
    }
    if (draft.parameters.some((p) => untouched(p) && !p.valueMatchesPlant)) {
      result.push({
        label: 'Valeur hors usine',
        severity: 'warn',
        tooltip: "Une valeur ne correspond pas à l'usine de la règle.",
      });
    }
    if (result.length === 0) {
      result.push({ label: 'À jour', severity: 'success' });
    }
    return result;
  }

  // ---- Drafts ----

  private newDraft(template: GainFormulaTemplate): DraftInstance {
    const names = this.extractReferences(template.equation);
    const parameters = names
      .map((name) => this.catalog.parameters.find((p) => p.name === name))
      .filter((p): p is GainParameter => !!p)
      .map((p) => {
        const draftParameter: DraftParameter = {
          parameterUid: p.uid,
          name: p.name,
          type: p.type,
          selectedValueUid: null,
          originalValueUid: null,
          typedValue: null,
          options: [],
          parameterIsDeleted: false,
          valueIsDeleted: false,
          valueMatchesPlant: true,
        };
        draftParameter.options = this.optionsFor(draftParameter);
        return draftParameter;
      });

    return {
      uid: null,
      templateUid: template.uid,
      templateGroupId: template.groupId,
      templateName: template.name,
      typePath: this.typePath(template.typeFK),
      equation: template.equation,
      templateVersion: template.version,
      latestVersion: template.version,
      templateIsDeleted: false,
      parameters,
    };
  }

  private fromInstance(instance: GainFormulaInstance): DraftInstance {
    return {
      uid: instance.uid,
      templateUid: instance.gainFormulaTemplateFK,
      templateGroupId: instance.templateGroupId,
      templateName: instance.templateName,
      typePath: instance.templateTypePath,
      equation: instance.equation,
      templateVersion: instance.templateVersion,
      latestVersion: instance.latestTemplateVersion,
      templateIsDeleted: instance.templateIsDeleted,
      parameters: instance.parameters.map((p) => {
        const draftParameter: DraftParameter = {
          parameterUid: p.gainParameterFK,
          name: p.parameterName,
          type: p.parameterType,
          selectedValueUid: p.gainParameterValueFK,
          originalValueUid: p.gainParameterValueFK,
          typedValue: p.parameterType === 'Decimal' ? p.value : null,
          options: [],
          parameterIsDeleted: p.parameterIsDeleted,
          valueIsDeleted: p.valueIsDeleted,
          valueMatchesPlant: p.valueMatchesRulePlant,
        };
        draftParameter.options = this.optionsFor(draftParameter);

        // Keep the stored selection visible even when it is no longer offered.
        if (
          p.gainParameterValueFK &&
          !draftParameter.options.some(
            (o) => o.value === p.gainParameterValueFK,
          )
        ) {
          const suffix = p.valueIsDeleted
            ? ' (supprimée)'
            : !p.valueMatchesRulePlant
              ? ' (autre usine)'
              : '';
          draftParameter.options = [
            {
              label: `${p.valueLabel ?? p.field} — ${p.value ?? ''}${suffix}`,
              value: p.gainParameterValueFK,
            },
            ...draftParameter.options,
          ];
        }
        return draftParameter;
      }),
    };
  }

  private optionsFor(parameter: DraftParameter): DraftOption[] {
    if (parameter.type === 'Decimal') return [];

    const plant = this.plant;
    const catalogParameter = this.catalog.parameters.find(
      (p) => p.uid === parameter.parameterUid,
    );

    return (catalogParameter?.values ?? [])
      .filter(
        (v) => !v.isDeleted && (v.factoryFK === null || v.factoryFK === plant),
      )
      .sort((a, b) => {
        const aSpecific = a.factoryFK ? 0 : 1;
        const bSpecific = b.factoryFK ? 0 : 1;
        return aSpecific - bSpecific || a.label.localeCompare(b.label);
      })
      .map((v) => ({
        label: `${v.label} — ${v.value} — ${
          v.factoryFK ? (v.factoryDescription ?? v.factoryFK) : ALL_PLANTS_LABEL
        }`,
        value: v.uid,
      }));
  }

  // ---- Tree ----

  private rebuildTemplateNodes(): void {
    const usedGroups = new Set(this.drafts.map((d) => d.templateGroupId));
    const names = new Set(this.catalog.parameters.map((p) => p.name));

    const childrenByParent = new Map<
      string | null,
      GainFormulaTemplateType[]
    >();
    for (const type of this.catalog.types) {
      const siblings = childrenByParent.get(type.parentFK) ?? [];
      siblings.push(type);
      childrenByParent.set(type.parentFK, siblings);
    }

    const templateNode = (template: GainFormulaTemplate): TreeNode => {
      const unresolved =
        template.deletedParameters.length > 0 ||
        template.unknownParameters.length > 0 ||
        this.extractReferences(template.equation).some((n) => !names.has(n));
      const reason = usedGroups.has(template.groupId)
        ? 'déjà ajoutée'
        : unresolved
          ? 'paramètre supprimé'
          : '';

      return {
        key: `tpl:${template.groupId}`,
        label: reason ? `${template.name} (${reason})` : template.name,
        data: template,
        leaf: true,
        selectable: !reason,
      };
    };

    const build = (parent: string | null): TreeNode[] =>
      (childrenByParent.get(parent) ?? [])
        .map((type) => {
          const children = [
            ...build(type.uid),
            ...this.catalog.templates
              .filter((t) => t.typeFK === type.uid)
              .map(templateNode),
          ];
          const addable = children.flatMap((c) =>
            this.collectSelectableTemplates(c),
          ).length;
          return {
            key: type.uid,
            label: addable > 0 ? `${type.name} (${addable})` : type.name,
            selectable: addable > 0,
            expanded: true,
            children,
          } as TreeNode;
        })
        .filter((node) => (node.children?.length ?? 0) > 0);

    this.templateNodes = build(null);
  }

  private collectSelectableTemplates(node: TreeNode): GainFormulaTemplate[] {
    if (node.leaf) {
      return node.selectable ? [node.data as GainFormulaTemplate] : [];
    }
    return (node.children ?? []).flatMap((c) =>
      this.collectSelectableTemplates(c),
    );
  }

  private typePath(typeUid: string): string {
    const byUid = new Map(this.catalog.types.map((t) => [t.uid, t]));
    const names: string[] = [];
    let current = byUid.get(typeUid);
    while (current) {
      names.unshift(current.name);
      current = current.parentFK ? byUid.get(current.parentFK) : undefined;
    }
    return names.join(' › ');
  }

  private extractReferences(equation: string): string[] {
    const names: string[] = [];
    for (const match of equation.matchAll(/\[([^\[\]]+)\]/g)) {
      if (!names.includes(match[1])) names.push(match[1]);
    }
    return names;
  }

  // ---- State ----

  private isComplete(parameter: DraftParameter): boolean {
    return parameter.type === 'Decimal'
      ? parameter.typedValue !== null && parameter.typedValue !== undefined
      : !!parameter.selectedValueUid;
  }

  private emitState(): void {
    const requests: GainFormulaInstanceRequest[] = this.drafts.map((d) => ({
      uid: d.uid,
      gainFormulaTemplateFK: d.templateUid,
      parameters: d.parameters
        .filter((p) => this.isComplete(p))
        .map((p) => ({
          gainParameterFK: p.parameterUid,
          gainParameterValueFK:
            p.type === 'List' ? p.selectedValueUid : null,
          value: p.type === 'Decimal' ? p.typedValue : null,
        })),
    }));
    const hasErrors = this.drafts.some((d) =>
      d.parameters.some((p) => !this.isComplete(p)),
    );

    // Deferred so the parent never changes bound state during its own change detection pass.
    Promise.resolve().then(() =>
      this.stateChange.emit({
        requests,
        dirty: this.dirty,
        hasErrors,
        loaded: this.loaded || (this.mode === 'create' && !this.loadError),
      }),
    );
  }
}
